import axios from 'axios';
import * as API from '@siloreal:shared:api';

const LAB_PROTOCOL_IMPORT_ENDPOINT = '/lab/protocols/import/manualInput';
const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export type ImportLabProtocolServiceContract = {
  file: File;
  silobagId?: string;
  signal?: AbortSignal;
};

export type ImportLabProtocolResponse = {
  error: false;
  message: string;
  response: Array<{
    id: number;
    lab_template_id: number;
    protocol_number: string;
    laboratory: string;
    acquisition_date: string;
    protocol_date: string;
    description: string;
    excel_serial_number: string;
    silobag_id: string;
    version: number;
    user_id: number;
    created_at: string;
    updated_at: string;
    lab_templates: unknown;
    lab_protocols_results: unknown[];
    tags: unknown[];
  }>;
};

export type LabProtocolImportErrorDetails = {
  code: number;
  message: string;
  details?: unknown;
};

export type LabProtocolImportErrorResponse = {
  error: boolean;
  message?: string | LabProtocolImportErrorDetails;
};

export class LabProtocolImportValidationError extends Error {
  readonly file: Blob;

  constructor(file: Blob) {
    super('La planilla contiene errores de validación.');
    this.name = 'LabProtocolImportValidationError';
    this.file = file;
  }
}

export class LabProtocolImportApiError extends Error {
  readonly code?: number;
  readonly response?: LabProtocolImportErrorResponse;

  constructor(response?: LabProtocolImportErrorResponse) {
    const details = response?.message;
    const message =
      typeof details === 'string'
        ? details
        : details?.message || 'No se pudo importar la planilla de muestras de calidad.';

    super(message);
    this.name = 'LabProtocolImportApiError';
    this.code = typeof details === 'object' ? details.code : undefined;
    this.response = response;
  }
}

async function parseJsonResponse<T>(data: unknown): Promise<T | undefined> {
  if (data instanceof Blob) {
    const text = await data.text();
    if (!text.trim()) return undefined;

    try {
      return JSON.parse(text) as T;
    } catch {
      return undefined;
    }
  }

  if (typeof data === 'string') {
    if (!data.trim()) return undefined;

    try {
      return JSON.parse(data) as T;
    } catch {
      return undefined;
    }
  }

  if (data && typeof data === 'object') return data as T;

  return undefined;
}

function getResponseContentType(headers: unknown): string {
  if (!headers || typeof headers !== 'object') return '';

  const getHeader = (headers as { get?: (name: string) => unknown }).get;
  if (typeof getHeader === 'function') {
    const value = getHeader.call(headers, 'content-type');
    return typeof value === 'string' ? value.toLowerCase() : '';
  }

  const contentType = (headers as Record<string, unknown>)['content-type'];

  return typeof contentType === 'string' ? contentType.toLowerCase() : '';
}

function toValidationBlob(data: unknown): Blob {
  if (data instanceof Blob) return data;

  return new Blob([typeof data === 'string' ? data : (JSON.stringify(data) ?? '')], {
    type: EXCEL_MIME_TYPE,
  });
}

export async function importLabProtocolService(
  contract: ImportLabProtocolServiceContract
): Promise<ImportLabProtocolResponse | undefined> {
  const formData = new FormData();
  formData.append('inputFile', contract.file);

  const path = contract.silobagId ? `/${encodeURIComponent(contract.silobagId)}` : '';

  try {
    const { data } = await API.http.fetch.multipart.post(`${LAB_PROTOCOL_IMPORT_ENDPOINT}${path}`, {
      body: formData,
      signal: contract.signal,
    });
    const response = await parseJsonResponse<ImportLabProtocolResponse>(data);

    return response;
  } catch (error) {
    if (!axios.isAxiosError(error) || !error.response) throw error;

    const contentType = getResponseContentType(error.response.headers);
    const responseData = error.response.data;
    const isValidationFile =
      contentType.includes(EXCEL_MIME_TYPE) ||
      (responseData instanceof Blob && responseData.type.toLowerCase().includes(EXCEL_MIME_TYPE));

    if (isValidationFile) {
      throw new LabProtocolImportValidationError(toValidationBlob(responseData));
    }

    const response = await parseJsonResponse<LabProtocolImportErrorResponse>(responseData);
    throw new LabProtocolImportApiError(response);
  }
}
